import { Injectable, Type }                               from '@angular/core';
import { ModalDismissReasons, NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService }                               from '@ngx-translate/core';

import { AddCustomerFromBranchComponent }        from './customer.add-from-branch.component';
import { ApiService }                            from '../api/api.service';
import { BranchCustomersFiltersModel }           from './typings/customers.branch.filters';
import { ChangeCustomerComponent }               from './customer.change.component';
import { CodeTypeModel }                         from '../core/models/code-type';
import { CompanyMessagesModel }                  from '../companies/models/company.messages';
import { CompanyService }                        from '../companies/company.service';
import { ConfirmService }                        from '../messages/confirm/confirm.service';
import { CustomerBranchHistoryComponent }        from './customer.branch-history.component';
import { CustomerCopyFromBranchModel }           from './typings/customer.copy-from-branch';
import { CustomerModel }                         from './models/customer';
import { CustomersFiltersModel }                 from './typings/customers.filters';
import { CustomerStatementSettingsComponent }    from './customer.statement-settings.component';
import { CustomerStatementSettingsModel }        from './typings/customer.statement-settings';
import { EmailCustomerStatementComponent }       from './customer.email-statement.component';
import { InvoiceModel }                          from '../invoices/models/invoice';
import { InvoicesForPaymentComponent }           from '../invoices/invoices.for-payment.component';
import { MessengerService }                      from '../messages/messenger/messenger.service';
import { ResolveCustomerMatchesComponent }       from './customer.resolve-matches.component';
import { SendCustomerPortalInvitationComponent } from './customer.send-portal-invitation.component';
import { TableQueryParameters }                  from '../table/types/table-query-parameters';

@Injectable({
  providedIn: 'root'
})
export class CustomersService {
  public broadcastKey  = 'filterCustomers';
  public tableCacheKey = 'customers';

  constructor (
    private apiService      : ApiService,
    private companyService  : CompanyService,
    private confirmService  : ConfirmService,
    private messengerService: MessengerService,
    private modalService    : NgbModal,
    private translateService: TranslateService
  ) {}

  activate ( customer : CustomerModel ) : Promise<boolean> {
    return this.apiService.get('/customers/reactivate_deleted', customer.id);
  }

  change ( customer : CustomerModel ) : Promise<CustomerModel> {
    return this.openModal(ChangeCustomerComponent, {}, {
      customer
    });
  }

  confirmPortalInvitation ( customer : CustomerModel, sendAsSms = false ) : Promise<boolean> {
    if ( sendAsSms && !customer.mobile ) {
      return Promise.reject(this.translateService.instant('CUSTOMER_SPACE.CUSTOMER_PORTAL.SMS_NEEDED'));
    }

    return this.apiService.post('/customers/send_customer_portal_invitation', {
      customer_id: customer.id,
      sms        : sendAsSms
    });
  }

  copyFromBranch ( customer : CustomerModel, vehicleId ?: string ) : Promise<CustomerCopyFromBranchModel> {
    return this.apiService.get('/customers/copy_from_branch', vehicleId
      ? [
          customer.id,
          vehicleId
        ]
      : customer.id);
  }

  create ( customer : CustomerModel ) : Promise<CustomerModel> {
    return this.apiService.post('/customers', customer, 'customer');
  }

  createCustomerFromOtherBranch ( quickAdd = false ) : Promise<CustomerCopyFromBranchModel> {
    return this.openModal(AddCustomerFromBranchComponent, {
      windowClass: 'x-lg-modal'
    }, {
      searchTypes: this.getSearchTypes(quickAdd)
    });
  }

  delete ( customer : CustomerModel ) : Promise<boolean> {
    return this.confirmService
      .generic(this.translateService.instant('JS_SPACE.CONFIRM.DELETE_CUSTOMER'))
      .then(() => this.apiService.delete('/customer', customer.id));
  }

  email ( customer : CustomerModel, email : string, body : string ) : Promise<void> {
    return this.messengerService.email({
      attachment_type: 4,
      body           : body,
      email_address  : email || customer.email,
      name           : customer.display_name
    });
  }

  emailStatement ( customer : CustomerModel ) : Promise<void> {
    return this.companyService.getDefaultMessages()
      .then(( companyMessages : CompanyMessagesModel ) => this.openModal(EmailCustomerStatementComponent, {
        keyboard: true
      }, {
        companyMessages,
        customer
      }));
  }

  findMatchingCustomers ( customer : CustomerModel ) : Promise<Array<CustomerModel>> {
    return this.apiService.get('/branch_matching/find_matches_for_customer', customer.id);
  }

  get ( filters = {} as CustomersFiltersModel, params : TableQueryParameters = {} ) : Promise<Array<CustomerModel>> {
    return this.apiService.stream('/filters', [
      filters.value    || '*',
      'customers',
      params.offset    || 0,
      params.pageSize  || 10,
      params.field     || 'display_name',
      params.direction || 'asc',
      !!filters.archivedOnly
    ]);
  }

  getBranchCustomerInvoices ( customer : CustomerModel, params : TableQueryParameters = {} ) : Promise<Array<InvoiceModel>> {
    return this.getBranchCustomerInvoicesCount(customer)
      .then(( count : number ) => this.apiService.get('/customers/branch_invoice_list', [
        customer.id,
        params.offset    || 0,
        params.pageSize  || count,
        'P',
        'I',
        params.field     || 'post_date',
        params.direction || 'desc'
      ]));
  }

  getBranchCustomerInvoicesCount ( customer : CustomerModel ) : Promise<number> {
    return this.apiService.get('/customers/branch_invoice_count', [
      customer.id,
      'P',
      'I'
    ]);
  }

  getBranchCustomers ( filters : BranchCustomersFiltersModel = {}, params : TableQueryParameters = {} ) : Promise<Array<CustomerModel>> {
    return this.apiService.get('/customers/branch_find', [
      filters.searchType || 'email',
      filters.searchTerm || '',
      params.offset      || 0,
      params.pageSize    || 5,
      params.field       || 'first_name',
      params.direction   || 'asc'
    ], undefined, {
      skipEncoding: true
    });
  }

  getCount ( filters = {} as CustomersFiltersModel ) : Promise<number> {
    return this.apiService.get('filters/count', [
      filters.value || '*',
      'customers',
      !!filters.archivedOnly
    ]);
  }

  getSearchTypes ( quickAdd = false ) : Array<CodeTypeModel> {
    const searchTypes : Array<CodeTypeModel> = [
      {
        code       : 'customer_name',
        description: this.translateService.instant('JS_SPACE.COLUMNS.CUSTOMER_NAME')
      },
      {
        code       : 'email',
        description: this.translateService.instant('GENERAL_SPACE.FIELD.EMAIL')
      },
      {
        code       : 'mobile',
        description: this.translateService.instant('GENERAL_SPACE.FIELD.MOBILE')
      }
    ];

    if ( quickAdd ) {
      searchTypes.push({
        code       : 'plate_number',
        description: this.translateService.instant('VEHICLE_SPACE.PLATE_NUMBER', {
          rego: this.companyService.getLabels().rego
        })
      }, {
        code       : 'vin',
        description: this.translateService.instant('VEHICLE_SPACE.VIN')
      });
    }

    return searchTypes;
  }

  hasPaymentTerm ( customer : CustomerModel ) : boolean {
    return !!customer.default_payment_terms
      && customer.default_payment_terms !== 'NONE';
  }

  isBiller ( customer : CustomerModel ) : boolean {
    return customer.biller;
  }

  isBranchCustomer ( customer : CustomerModel ) : boolean {
    return !!customer.is_branch_customer;
  }

  isLinkedToHeadOffice ( customer : CustomerModel ) : boolean {
    return !!customer.branch_link;
  }

  isType ( customer : CustomerModel, type = 'company' ) : boolean {
    switch ( type ) {
      case 'individual':
        return customer.customer_type === 'I';

      case 'company':
      default:
        return customer.customer_type === 'C';
    }
  }

  lookupBranchCustomerInvoices ( customer : CustomerModel ) : Promise<Array<InvoiceModel>> {
    return this.getBranchCustomerInvoices(customer)
      .then(( invoices : Array<InvoiceModel> ) => this.openModal(InvoicesForPaymentComponent, {
        keyboard   : true,
        windowClass: 'x-lg-modal'
      }, {
        cacheKey    : 'branchCustomerInvoices',
        invoices,
        isStoreShown: this.isBranchCustomer(customer),
        loadFn      : ( params : TableQueryParameters ) => this.getBranchCustomerInvoices(customer, params)
      }));
  }

  printStatement ( customer : CustomerModel ) : Promise<void> {
    return this.openModal<CustomerStatementSettingsModel>(CustomerStatementSettingsComponent, {
      keyboard   : true,
      windowClass: 'md-modal'
    })
      .then(( settings : CustomerStatementSettingsModel ) => {
        const link = settings.statement
          ? `jasper_statements_306090/${ customer.id }/${ settings.as_at }`
          : `jasper_statements/${ customer.id }/${ settings.begin_date }/${ settings.end_date }/${ settings.include_paid }`;

        window.open(`${ this.apiService.getEndpoint() }/reports/${ link }.pdf`, '_blank');
      });
  }

  resolveMatches ( customer : CustomerModel, matches : Array<CustomerModel> ) : Promise<CustomerModel> {
    return this.openModal<CustomerModel>(ResolveCustomerMatchesComponent, {
      windowClass: 'x-lg-modal'
    }, {
      customer,
      matches
    })
      .catch(() => customer);
  }

  save ( customer : CustomerModel ) : Promise<CustomerModel> {
    const isNew = !customer.id;

    return ( isNew
      ? this.create(customer)
      : this.update(customer) )
      .then(( savedCustomer : CustomerModel ) => {
        if ( isNew && this.companyService.isSharedAccounting() && this.companyService.isAccountingSetup() ) {
          return this.findMatchingCustomers(savedCustomer)
            .then(matches => matches.length
              ? this.resolveMatches(savedCustomer, matches)
                .then(() => savedCustomer)
              : savedCustomer);
        }

        return savedCustomer;
      });
  }

  saveMatch ( customer : CustomerModel, match : CustomerModel ) : Promise<boolean> {
    return this.apiService.post('/branch_matching/link_customer_ids', [
      {
        accounting_id: match.accounting_id,
        id           : customer.id
      }
    ], 'links');
  }

  sendEmail ( customer : CustomerModel, message : string ) : Promise<void> {
    return this.messengerService.email({
      attachment_type: 4,
      body           : message,
      customer_id    : customer.id,
      email_address  : customer.email,
      name           : customer.company_name
    });
  }

  sendPortalInvitation ( customer : CustomerModel ) : Promise<void> {
    if ( !customer.email ) {
      return Promise.reject(this.translateService.instant('CUSTOMER_SPACE.CUSTOMER_PORTAL.EMAIL_NEEDED'));
    }

    return this.openModal(SendCustomerPortalInvitationComponent, {
      keyboard: true
    }, {
      customer
    });
  }

  sendSms ( customer : CustomerModel ) : Promise<void> {
    return this.messengerService.sms({
      customer_id: customer.id,
      name       : customer.display_name,
      send_to    : customer.mobile
    });
  }

  update ( customer : CustomerModel ) : Promise<CustomerModel> {
    return this.apiService.patch('/customer', customer, 'customer');
  }

  validateCompanyName ( name : string ) : Promise<boolean> {
    return this.apiService.post('/customers/verify_company_name_uniqueness', {
      company_name: name
    })
      .then(result => result.company_name_unique);
  }

  validateFirstAndLastName ( customer : CustomerModel, name : string, field = 'first_name' ) : Promise<boolean> {
    if ( field === 'first_name' && ( !name || !customer.last_name ) ) {
      return Promise.resolve(true);
    }

    if ( field === 'last_name' && ( !name || !customer.first_name ) ) {
      return Promise.resolve(true);
    }

    return this.apiService.post('/customers/verify_name_uniqueness', {
      first_name: field === 'first_name'
        ? name
        : customer.first_name,
      last_name: field === 'last_name'
        ? name
        : customer.last_name
    })
      .then(result => result.name_unique);
  }

  viewBranchHistory ( customer : CustomerModel ) : Promise<void> {
    return this.apiService.get('/customers/invoice_list', [
      customer.id,
      0,
      '*',
      '*',
      '*',
      'post_date',
      'desc'
    ])
      .then(( invoices : Array<InvoiceModel> ) => this.openModal(CustomerBranchHistoryComponent, {
        keyboard   : true,
        windowClass: 'x-lg-modal'
      }, {
        invoices
      }));
  }

  viewReport ( invoice : InvoiceModel ) : void {
    window.open(`${ this.apiService.getEndpoint() }/reports/jasper_invoice/${ invoice.id }.pdf`, '_blank');
  }

  // Cancel, Esc and a backdrop click reject with no reason, which callers' error handlers ignore.
  private openModal<T = void> ( component : Type<unknown>, options : NgbModalOptions = {}, inputs : Record<string, unknown> = {} ) : Promise<T> {
    const modal = this.modalService.open(component, options);

    Object.assign(modal.componentInstance, inputs);

    return modal.result
      .catch(reason => Promise.reject(
        reason === undefined || reason === ModalDismissReasons.ESC || reason === ModalDismissReasons.BACKDROP_CLICK
          ? undefined
          : reason
      ));
  }
}
